import { useEffect, useState } from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import { cityService } from "../../../services/cityService";

const TripDestinationsForm = ({
  t,
  lang,
  control,
  setValue,
  errors,
  countries,
}) => {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "tripDestinations",
    keyName: "fieldKey",
  });
  const destinationsWatch = useWatch({
    control,
    name: "tripDestinations",
  });
  // Keyed by the row's stable fieldKey (not array index), so a reorder
  // never leaves a row's city list attached to the wrong row.
  const [citiesByRow, setCitiesByRow] = useState({});

  const handleAdd = () => {
    append({
      countryId: "",
      cityId: "",
      order: fields.length + 1,
      days: "",
    });
  };

  const handleRemove = (index) => {
    const removedKey = fields[index]?.fieldKey;
    remove(index);
    if (removedKey) {
      setCitiesByRow((prev) => {
        const next = { ...prev };
        delete next[removedKey];
        return next;
      });
    }
    setValue(
      "tripDestinations",
      (destinationsWatch || [])
        .filter((_, i) => i !== index)
        .map((d, i) => ({ ...d, order: i + 1 })),
      { shouldValidate: true },
    );
  };

  const moveRow = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= fields.length) return;
    move(index, target);
    setTimeout(() => {
      const current = destinationsWatch || [];
      current.forEach((_, i) => {
        setValue(`tripDestinations.${i}.order`, i + 1);
      });
    }, 0);
  };

  const loadCitiesForRow = async (fieldKey, countryId) => {
    if (!countryId) {
      setCitiesByRow((prev) => ({ ...prev, [fieldKey]: [] }));
      return;
    }
    const data = await cityService.getDropdownByCountryAsync(countryId);
    setCitiesByRow((prev) => ({ ...prev, [fieldKey]: data }));
  };

  useEffect(() => {
    fields.forEach((field, index) => {
      const countryId = destinationsWatch?.[index]?.countryId;
      if (countryId && !citiesByRow[field.fieldKey]) {
        loadCitiesForRow(field.fieldKey, countryId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.map((f) => `${f.fieldKey}:${f.countryId}`).join(",")]);

  return (
    <div className="my-card mt-3">
      <div className="my-card-header text-primary">
        {t("tripDestinations")}
      </div>
      <div className="my-card-body">
        {fields?.map((field, index) => {
          const row = destinationsWatch?.[index] || {};
          const rowCities = citiesByRow[field.fieldKey] || [];
          return (
            <div className="my-card mt-3" key={field.fieldKey}>
              <div className="row align-items-start">
                <div className="col-12">
                  <span className="fw-bold">#{index + 1}</span>
                </div>
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label">{t("country")}</label>
                    <select
                      className="form-control"
                      value={row.countryId || ""}
                      onChange={(e) => {
                        const newCountryId = e.target.value;
                        setValue(
                          `tripDestinations.${index}.countryId`,
                          newCountryId,
                          { shouldValidate: true },
                        );
                        setValue(`tripDestinations.${index}.cityId`, "", {
                          shouldValidate: true,
                        });
                        loadCitiesForRow(field.fieldKey, newCountryId);
                      }}
                    >
                      <option value="">--{t("country")}--</option>
                      {countries &&
                        countries.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name[lang]}
                          </option>
                        ))}
                    </select>
                    <p className="text-danger">
                      {errors?.tripDestinations?.[index]?.countryId?.message}
                    </p>
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label">{t("city")}</label>
                    <select
                      className="form-control"
                      value={row.cityId || ""}
                      onChange={(e) => {
                        setValue(
                          `tripDestinations.${index}.cityId`,
                          e.target.value,
                          { shouldValidate: true },
                        );
                      }}
                      disabled={!row.countryId}
                    >
                      <option value="">--{t("city")}--</option>
                      {rowCities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name[lang]}
                        </option>
                      ))}
                    </select>
                    <p className="text-danger">
                      {errors?.tripDestinations?.[index]?.cityId?.message}
                    </p>
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label">{t("daysInThisCity")}</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      value={row.days ?? ""}
                      onChange={(e) => {
                        setValue(
                          `tripDestinations.${index}.days`,
                          e.target.value,
                          { shouldValidate: true },
                        );
                      }}
                    />
                    <p className="text-danger">
                      {errors?.tripDestinations?.[index]?.days?.message}
                    </p>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label d-none d-md-block invisible">
                    &nbsp;
                  </label>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={index === 0}
                      onClick={() => moveRow(index, -1)}
                    >
                      <i className="fas fa-arrow-up" />
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={index === fields.length - 1}
                      onClick={() => moveRow(index, 1)}
                    >
                      <i className="fas fa-arrow-down" />
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => handleRemove(index)}
                    >
                      <i className="fas fa-trash" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {typeof errors?.tripDestinations?.message === "string" && (
          <p className="text-danger">{errors.tripDestinations.message}</p>
        )}
        <button type="button" className="btn-default mt-2" onClick={handleAdd}>
          <i className="fas fa-plus" /> {t("addDestination")}
        </button>
      </div>
    </div>
  );
};

export default TripDestinationsForm;
